import React from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { LayoutDashboard, FileText, Upload, LogOut, ExternalLink, Users, Activity, Webhook, Search, BarChart3, Syringe } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

export default function AdminLayout({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const onLogout = async () => {
        await logout();
        navigate("/admin/login");
    };

    const navItem = ({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-colors ${isActive ? "bg-[#2F80ED]/10 text-white border border-[#2F80ED]/30" : "text-[#94A3B8] hover:text-white hover:bg-[#111827] border border-transparent"}`;

    return (
        <div className="min-h-screen bg-[#0B1220] flex">
            <aside className="w-64 shrink-0 bg-[#0B1220] border-r border-[#243044] sticky top-0 h-screen flex flex-col">
                <Link to="/admin" className="flex items-center gap-3 px-6 py-6 border-b border-[#243044]">
                    <div className="h-9 px-2 flex items-center justify-center rounded-md bg-white"><img src={require("../../assets/logo-tcp-mark.png")} alt="The Context Project" className="h-6 w-auto select-none" draggable={false} /></div>
                    <div>
                        <div className="text-sm font-bold text-white leading-tight">Admin</div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#64748B]">Context Project</div>
                    </div>
                </Link>

                <nav className="flex-1 p-4 space-y-1.5">
                    <NavLink to="/admin" end className={navItem} data-testid="admin-nav-dashboard">
                        <LayoutDashboard className="w-4 h-4" />
                        Dashboard
                    </NavLink>
                    <NavLink to="/admin/topics/new" className={navItem} data-testid="admin-nav-new-topic">
                        <FileText className="w-4 h-4" />
                        New Topic
                    </NavLink>
                    <NavLink to="/admin/csv" className={navItem} data-testid="admin-nav-csv">
                        <Upload className="w-4 h-4" />
                        CSV Import / Export
                    </NavLink>
                    <NavLink to="/admin/members" className={navItem} data-testid="admin-nav-members">
                        <Users className="w-4 h-4" />
                        Members
                    </NavLink>
                    <div className="pt-3 mt-3 border-t border-[#243044]">
                        <div className="px-4 pb-2 text-[10px] uppercase tracking-[0.22em] text-[#64748B]">Research Explorer</div>
                    </div>
                    <NavLink to="/admin/vaccines" className={navItem} data-testid="admin-nav-vaccines">
                        <Syringe className="w-4 h-4" />
                        Vaccines
                    </NavLink>
                    <div className="pt-3 mt-3 border-t border-[#243044]">
                        <div className="px-4 pb-2 text-[10px] uppercase tracking-[0.22em] text-[#64748B]">Operations</div>
                    </div>
                    <NavLink to="/admin/webhooks" className={navItem} data-testid="admin-nav-webhooks">
                        <Webhook className="w-4 h-4" />
                        Webhooks
                    </NavLink>
                    <NavLink to="/admin/health" className={navItem} data-testid="admin-nav-health">
                        <Activity className="w-4 h-4" />
                        System health
                    </NavLink>
                    <NavLink to="/admin/search-intel" className={navItem} data-testid="admin-nav-search-intel">
                        <Search className="w-4 h-4" />
                        Search intel
                    </NavLink>
                    <NavLink to="/admin/analytics" className={navItem} data-testid="admin-nav-analytics">
                        <BarChart3 className="w-4 h-4" />
                        Analytics
                    </NavLink>
                </nav>

                <div className="p-4 border-t border-[#243044] space-y-3">
                    <a
                        href="/"
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-2 text-xs text-[#64748B] hover:text-[#56CCF2] transition-colors"
                    >
                        <ExternalLink className="w-3 h-3" />
                        View site
                    </a>
                    <div className="text-xs text-[#94A3B8] truncate" title={user?.email}>
                        {user?.email}
                    </div>
                    <button
                        data-testid="admin-logout"
                        onClick={onLogout}
                        className="flex items-center gap-2 text-xs text-[#94A3B8] hover:text-white transition-colors"
                    >
                        <LogOut className="w-3 h-3" />
                        Logout
                    </button>
                </div>
            </aside>

            <main className="flex-1 min-w-0">{children}</main>
        </div>
    );
}
